import { DurationUnit } from 'Services/ProductManagement/Features/Product/types'
import {
  BACKEND_DATE_TIME_FORMAT,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_WITHOUT_SECONDS
} from 'Util/constants'
import { Buffer } from 'buffer'
import { format, secondsToMinutes } from 'date-fns'
import { fi } from 'date-fns/locale'
import { c } from 'ttag'

const datetimeFormatter = (value: Date | null) => {
  if (value == null) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return format(date, DATE_TIME_FORMAT, { locale: fi })
}

const datetimeFormatterWithoutSeconds = (value: Date | null) => {
  if (value == null) return '-'
  return format(new Date(value), DATE_TIME_FORMAT_WITHOUT_SECONDS, { locale: fi })
}

const toBackendDateTime = (value: Date | null): string => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return ''
  return dateFormatter(value, BACKEND_DATE_TIME_FORMAT).toString()
}

const dateFormatter = (value: Date, dateFormat: string = DATE_FORMAT) => {
  if (Number.isNaN(value.getTime())) return ''
  return format(new Date(value), dateFormat, { locale: fi })
}

const formatToUTCISO = (dateWithTimeZone: Date) => {
  // date pickers can emit an invalid Date (still `instanceof Date`) while typing, guard against it
  if (!(dateWithTimeZone instanceof Date) || Number.isNaN(dateWithTimeZone.getTime())) return ''
  // toISOString() converts the date to UTC timezone
  return dateWithTimeZone.toISOString()
}

const formatToTimeZoneAwareISO = (date: Date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  // Convert the date to a timezone-aware ISO string
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
}

const isDateTodayOrInThePast = (date: Date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const inputDate = new Date(date)
  inputDate.setHours(0, 0, 0, 0)
  return inputDate <= today
}

function capitalizeFirstLetter(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const replaceHyphenWithSpace = (value: string | null) => {
  if (value == null) return '-'
  return capitalizeFirstLetter(value.replace(/-/g, ' '))
}

function showValueOrHyphen(value: string | number | null | undefined, symbol?: string) {
  if (value == null || value === '') return '-'
  const returnValue = symbol ? `${value} ${symbol}` : value
  return returnValue.toString()
}

function secondsToDays(value: number) {
  return Math.floor(value / 86400)
}

function convertToAppropriateUnits(value: number | undefined) {
  /**
   * Converts a given value into appropriate units (days, minutes, or seconds).
   *
   * @param {number | undefined} value - The value to convert.
   * @returns {string} The converted value with appropriate units.
   */
  if (value === undefined) return '-'
  if (secondsToDays(value) > 0) return `${secondsToDays(value)} ${c('unit').t`d`}`
  if (secondsToMinutes(value) > 0) return `${secondsToMinutes(value)} ${c('unit').t`min`}`
  return `${value} ${c('unit').t`s`}`
}

interface MultiTypeOption {
  label: string
  value: string | boolean
}

interface Option {
  label: string
  value: string
}
const getValueFromOptions = (
  value: string | boolean,
  options: MultiTypeOption[],
  fallbackLabel?: string
) => {
  const foundOption = options.find(option => option.value === value)
  return foundOption ? foundOption.label : (fallbackLabel ?? '?')
}

const getValuesFromOptions = (values: string[], options: Option[], fallbackLabel?: string) => {
  const foundOptions = options.filter(option => values.includes(option.value))

  // If there are no corresponding options for all the values, return fallbackLabel or '?'
  if (foundOptions.length !== values.length) return fallbackLabel ?? '?'
  return foundOptions.map(option => option.label).join(', ')
}

const formatViolationErrorField = (field: string) => {
  /**
   * Converts a given field error from a violation object into a form error format.
   * For example, 'createServiceProvider.serviceProvider.startDate' -> 'start_date'.
   *
   * @param string - The value to convert.
   * @returns string - The converted error field.
   */

  try {
    const fieldParts = field.split('.')
    const errorField = fieldParts[fieldParts.length - 1]
    return errorField.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()
  } catch {
    return field
  }
}

/**
 * Converts an ISO 8601 duration string into an object containing hours and minutes.
 * Only hours and minutes are taken into account since P&T endpoint durations are in the format
 * PTxHxM, PTxH or PTxM.
 *
 * @param duration - The ISO 8601 duration string to convert.
 * @returns An object with hours and minutes extracted from the duration.
 */
const convertISODurationToTimeUnits = (duration: string) => {
  const regex = /P(?:T)?(?:(\d+)D)?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/
  const matches = RegExp(regex).exec(duration)

  if (!matches) {
    throw new Error('Invalid ISO 8601 duration format')
  }

  const hours = matches[2] ? parseInt(matches[2], 10) : 0
  const minutes = matches[3] ? parseInt(matches[3], 10) : 0

  return {
    hours,
    minutes
  }
}

/**
 * Converts an ISO 8601 duration string into a human-readable format.
 *
 * @param duration - The ISO 8601 duration string to convert.
 * @returns A human-readable string representing the duration.
 */
const isoDurationToHumanReadableFormat = (duration: string | null) => {
  if (!duration) return '-'

  const minutesUnit = c('unit').t`min`
  const daysUnit = c('unit').t`d`
  const hoursUnit = c('unit').t`h`
  const timeUnits = convertISODurationToTimeUnits(duration)

  if (timeUnits.hours > 0 && timeUnits.minutes > 0) {
    const hoursToMinutes = timeUnits.hours * 60
    const totalMinutes = hoursToMinutes + timeUnits.minutes
    return `${totalMinutes} ${minutesUnit}`
  } else if (timeUnits.hours > 0 && timeUnits.hours % 24 === 0) {
    return `${timeUnits.hours / 24} ${daysUnit}`
  } else if (timeUnits.hours > 0) {
    return `${timeUnits.hours} ${hoursUnit}`
  } else if (timeUnits.minutes > 0) {
    return `${timeUnits.minutes} ${minutesUnit}`
  }
}

/**
 * Calculates the length of an ISO duration and returns the duration format.
 *
 * Checks if the duration has hours and minutes, and if so, converts the hours to minutes.
 * If the duration is in hours and is a multiple of 24, it converts it to days.
 * If the duration is only in hours, it returns the hours.
 * If the duration is only in minutes, it returns the minutes.
 *
 * @param value - The ISO duration string to be calculated.
 * @returns An object containing the length and unit of the duration.
 */
const calculateISODurationLength = (value: string) => {
  const timeUnits = convertISODurationToTimeUnits(value)

  if (timeUnits.hours > 0 && timeUnits.minutes > 0) {
    const hoursToMinutes = timeUnits.hours * 60
    const totalMinutes = hoursToMinutes + timeUnits.minutes
    return { length: totalMinutes, unit: DurationUnit.MINUTES }
  } else if (timeUnits.hours > 0 && timeUnits.hours % 24 === 0) {
    return { length: timeUnits.hours / 24, unit: DurationUnit.DAYS }
  } else if (timeUnits.hours > 0) {
    return { length: timeUnits.hours, unit: DurationUnit.HOURS }
  } else if (timeUnits.minutes > 0) {
    return { length: timeUnits.minutes, unit: DurationUnit.MINUTES }
  } else {
    return { length: 0, unit: DurationUnit.MINUTES }
  }
}

const calculateISODurationLengthInMinutes = (value: string) => {
  const timeUnits = convertISODurationToTimeUnits(value)

  const totalMinutes = timeUnits.hours * 60 + timeUnits.minutes
  return totalMinutes
}

/**
 * Converts a Base64-encoded string into a binary string.
 *
 * This function decodes the provided Base64 string into a binary string,
 * which can be useful for QR encoding or other binary data processing.
 */
const base64ToBinaryString = (base64EncodedString: string): string => {
  try {
    const buffer = Buffer.from(base64EncodedString, 'base64')
    return buffer.toString('binary')
  } catch {
    throw new Error('Invalid base64 input.')
  }
}

/**
 * Converts a time string in format "hh:mm:ss" to a Date object using the current date
 * @param timeString Time string in format "hh:mm:ss"
 * @returns Date object with the specified time
 */
const timeStringToDate = (timeString: string): Date | null => {
  if (!timeString) return null

  const [hours, minutes, seconds] = timeString.split(':').map(Number)

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return null
  }

  const date = new Date()
  date.setHours(hours)
  date.setMinutes(minutes)
  date.setSeconds(seconds)

  return date
}

/**
 * Creates a duration map for ISO duration strings based on the time unit and value
 * @param value - The numeric value for the duration
 * @returns A mapping of time units to ISO duration strings
 */
const createDurationMap = (value: number): Record<string, string> => {
  return {
    days: `PT${value * 24}H`,
    hours: `PT${value}H`,
    minutes: `PT${value}M`
  }
}

/**
 * Parses a string into a positive integer or returns undefined if the value is not a valid positive integer.
 * @param value - The string value to be parsed.
 * @returns The parsed positive integer or undefined.
 */
const parsePositiveIntegerOrUndefined = (value: string | null | undefined): number | undefined => {
  if (value === undefined || value === null) return undefined
  const parsedValue = Number.parseInt(value, 10)
  return !Number.isNaN(parsedValue) && parsedValue > 0 ? parsedValue : undefined
}

export {
  base64ToBinaryString,
  calculateISODurationLength,
  calculateISODurationLengthInMinutes,
  convertISODurationToTimeUnits,
  convertToAppropriateUnits,
  createDurationMap,
  dateFormatter,
  datetimeFormatter,
  datetimeFormatterWithoutSeconds,
  formatToTimeZoneAwareISO,
  formatToUTCISO,
  formatViolationErrorField,
  getValueFromOptions,
  getValuesFromOptions,
  isDateTodayOrInThePast,
  isoDurationToHumanReadableFormat,
  parsePositiveIntegerOrUndefined,
  replaceHyphenWithSpace,
  secondsToDays,
  showValueOrHyphen,
  timeStringToDate,
  toBackendDateTime
}
